/**
 * NetworkSignalCollector
 *
 * Collects on-device network signals for cross-device identity resolution:
 *   - Local IP hash (SHA-256, hashed on-device before leaving the browser)
 *   - navigator.connection signals (type, effectiveType, downlink, rtt)
 *
 * Graceful degradation: returns null/partial results if WebRTC or
 * navigator.connection are unavailable (e.g. restricted WebViews).
 */

/**
 * SHA-256 hash using the Web Crypto API (available in all modern WebViews).
 * @param {string} input - plaintext to hash
 * @returns {Promise<string>} hex-encoded SHA-256 digest
 */
async function sha256(input) {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * RFC 1918 / RFC 4193 private IP regex.
 * Matches 10.x.x.x, 172.16-31.x.x, 192.168.x.x
 */
const PRIVATE_IP_RE = /^(?:10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(?:1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}|192\.168\.\d{1,3}\.\d{1,3})$/;

/**
 * Use RTCPeerConnection with a STUN server to discover the local (private) IP.
 * Returns the first private IP found, or null if WebRTC is blocked / unavailable.
 *
 * @param {number} [timeoutMs=3000] - max time to wait for ICE candidates
 * @returns {Promise<string|null>} private IP address or null
 */
function discoverLocalIP(timeoutMs = 3000) {
  return new Promise((resolve) => {
    // Guard: WebRTC not available
    if (typeof RTCPeerConnection === 'undefined') {
      resolve(null);
      return;
    }

    let resolved = false;
    const finish = (ip) => {
      if (resolved) return;
      resolved = true;
      try { pc.close(); } catch (_) { /* noop */ }
      resolve(ip);
    };

    let pc;
    try {
      pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });
    } catch (_) {
      resolve(null);
      return;
    }

    // Timeout fallback
    const timer = setTimeout(() => finish(null), timeoutMs);

    pc.onicecandidate = (event) => {
      if (!event || !event.candidate || !event.candidate.candidate) return;

      const parts = event.candidate.candidate.split(' ');
      // ICE candidate format: "candidate:... typ host ..." — IP is at index 4
      const ip = parts[4];
      if (ip && PRIVATE_IP_RE.test(ip)) {
        clearTimeout(timer);
        finish(ip);
      }
    };

    // Also check the SDP for embedded candidates (some browsers)
    pc.onicegatheringstatechange = () => {
      if (pc.iceGatheringState === 'complete' && !resolved) {
        // Parse the local description SDP for candidate lines
        const sdp = pc.localDescription?.sdp || '';
        const lines = sdp.split('\n');
        for (const line of lines) {
          if (!line.startsWith('a=candidate:')) continue;
          const parts = line.split(' ');
          const ip = parts[4];
          if (ip && PRIVATE_IP_RE.test(ip)) {
            clearTimeout(timer);
            finish(ip);
            return;
          }
        }
        // No private IP found in SDP either
        clearTimeout(timer);
        finish(null);
      }
    };

    try {
      pc.createDataChannel('');
      pc.createOffer()
        .then(offer => pc.setLocalDescription(offer))
        .catch(() => {
          clearTimeout(timer);
          finish(null);
        });
    } catch (_) {
      clearTimeout(timer);
      finish(null);
    }
  });
}

/**
 * Collect navigator.connection signals if available.
 * @returns {{ type: string|null, effectiveType: string|null, downlink: number|null, rtt: number|null }}
 */
function getConnectionSignals() {
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!conn) {
    return { type: null, effectiveType: null, downlink: null, rtt: null };
  }
  return {
    type: conn.type || null,              // e.g. "wifi", "cellular", "ethernet"
    effectiveType: conn.effectiveType || null,  // e.g. "4g", "3g"
    downlink: conn.downlink != null ? conn.downlink : null,  // Mbps
    rtt: conn.rtt != null ? conn.rtt : null,                 // ms
  };
}

/**
 * Collect all network signals for cross-device identity resolution.
 *
 * @returns {Promise<{ localIpHash: string|null, networkType: string|null, downlinkMbps: number|null, rttMs: number|null }>}
 */
export async function collectNetworkSignals() {
  try {
    // Run IP discovery and connection signal collection in parallel
    const [localIP, connSignals] = await Promise.all([
      discoverLocalIP(),
      Promise.resolve(getConnectionSignals()),
    ]);

    const localIpHash = localIP ? await sha256(localIP) : null;

    // Prefer connection.type ("wifi"/"ethernet"), fall back to effectiveType ("4g"/"3g")
    const networkType = connSignals.type || connSignals.effectiveType || null;

    return {
      localIpHash,
      networkType,
      downlinkMbps: connSignals.downlink,
      rttMs: connSignals.rtt,
    };
  } catch (_) {
    // Complete failure — return all nulls so callers degrade gracefully
    return {
      localIpHash: null,
      networkType: null,
      downlinkMbps: null,
      rttMs: null,
    };
  }
}
